import React from 'react';
import { motion } from 'framer-motion';
import { 
  Mic, 
  MicOff, 
  Video, 
  VideoOff, 
  RotateCcw, 
  Volume2, 
  VolumeX,
  Loader2,
  AlertTriangle,
  CheckCircle,
  Camera
} from 'lucide-react';
import Button from '../common/Button';
import { MediaDevicesState } from '../../hooks/useMediaDevices';

interface MobileSessionControlsProps {
  mediaState: MediaDevicesState;
  onStartCamera: () => void;
  onStartMicrophone: () => void;
  onToggleCamera: () => void;
  onInitializeAudio: () => void;
  isSessionActive: boolean;
}

const MobileSessionControls: React.FC<MobileSessionControlsProps> = ({
  mediaState,
  onStartCamera,
  onStartMicrophone,
  onToggleCamera,
  onInitializeAudio,
  isSessionActive,
}) => {
  const {
    isCameraLoading,
    isMicLoading,
    cameraError,
    micError,
    cameraActive,
    micActive,
    isRearCamera,
    audioBlocked,
    facingMode,
  } = mediaState;

  return (
    <div className="space-y-4">
      {/* Audio Blocked Warning */}
      {audioBlocked && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-warning-50 border border-warning-200 rounded-lg p-4"
        >
          <div className="flex items-center">
            <VolumeX className="h-5 w-5 text-warning-600 mr-3" />
            <div className="flex-1">
              <p className="text-warning-800 font-medium">Sound blocked</p>
              <p className="text-warning-700 text-sm">Tap anywhere to enable voice feedback</p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={onInitializeAudio}
              className="border-warning-300 text-warning-700 hover:bg-warning-50"
              leftIcon={<Volume2 className="h-4 w-4" />}
            >
              Enable Audio
            </Button>
          </div>
        </motion.div>
      )}

      {/* Camera Controls */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-medium text-gray-800 flex items-center">
            <Camera className="h-5 w-5 mr-2 text-primary-600" />
            Camera
          </h3>
          {cameraActive && (
            <div className="flex items-center space-x-2">
              <CheckCircle className="h-4 w-4 text-success-500" />
              <span className="text-sm text-success-600 font-medium">
                Active ({facingMode === 'user' ? 'Front' : 'Rear'})
              </span>
            </div>
          )}
        </div>

        {isCameraLoading && (
          <div className="flex items-center justify-center py-8 bg-gray-50 rounded-lg">
            <Loader2 className="h-6 w-6 animate-spin text-primary-600 mr-3" />
            <div className="text-center">
              <p className="text-gray-600 font-medium">Loading camera...</p>
              <p className="text-gray-500 text-sm">Please wait or enable permissions</p>
            </div>
          </div>
        )}

        {cameraError && (
          <div className="bg-error-50 border border-error-200 rounded-lg p-3 mb-3">
            <div className="flex items-start">
              <AlertTriangle className="h-5 w-5 text-error-600 mr-2 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-error-800 text-sm font-medium">Camera Error</p>
                <p className="text-error-700 text-sm mt-1">{cameraError}</p>
                <div className="mt-2 text-xs text-error-600">
                  <p className="font-medium">Troubleshooting steps:</p>
                  <ul className="list-disc list-inside mt-1 space-y-1">
                    <li>Refresh the page and allow camera access</li>
                    <li>Check if another app is using the camera</li>
                    <li>Try switching to a different camera</li>
                    <li>Ensure your browser supports camera access</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="flex space-x-2">
          {!cameraActive ? (
            <Button
              variant="primary"
              onClick={onStartCamera}
              disabled={isCameraLoading}
              leftIcon={isCameraLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Video className="h-4 w-4" />}
              className="flex-1"
            >
              {isCameraLoading ? 'Starting Camera...' : 'Turn On Camera'}
            </Button>
          ) : (
            <>
              <div className="flex-1 flex items-center text-success-600 bg-success-50 rounded-lg px-3 py-2">
                <Video className="h-4 w-4 mr-2" />
                <span className="text-sm font-medium">
                  🎥 Camera Active ({facingMode === 'user' ? 'Front' : 'Rear'})
                </span>
              </div>
              <Button
                variant="outline"
                onClick={onToggleCamera}
                disabled={isCameraLoading}
                leftIcon={<RotateCcw className="h-4 w-4" />}
                className="px-3"
                title="Flip Camera"
              >
                {isCameraLoading ? '...' : '🔄 Flip'}
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Microphone Controls */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-medium text-gray-800 flex items-center">
            <Mic className="h-5 w-5 mr-2 text-primary-600" />
            Microphone
          </h3>
          {micActive && (
            <div className="flex items-center space-x-2">
              <CheckCircle className="h-4 w-4 text-success-500" />
              <span className="text-sm text-success-600 font-medium">Active</span>
            </div>
          )}
        </div>

        {isMicLoading && (
          <div className="flex items-center justify-center py-4 bg-gray-50 rounded-lg">
            <Loader2 className="h-5 w-5 animate-spin text-primary-600 mr-2" />
            <span className="text-gray-600">Starting microphone...</span>
          </div>
        )}

        {micError && (
          <div className="bg-error-50 border border-error-200 rounded-lg p-3 mb-3">
            <div className="flex items-start">
              <AlertTriangle className="h-5 w-5 text-error-600 mr-2 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-error-800 text-sm font-medium">Microphone Error</p>
                <p className="text-error-700 text-sm mt-1">{micError}</p>
                <div className="mt-2 text-xs text-error-600">
                  <p className="font-medium">Try:</p>
                  <ul className="list-disc list-inside mt-1">
                    <li>Allow microphone access in browser settings</li>
                    <li>Refresh the page and try again</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}

        {!micActive ? (
          <Button
            variant="primary"
            onClick={onStartMicrophone}
            disabled={isMicLoading}
            leftIcon={isMicLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mic className="h-4 w-4" />}
            fullWidth
          >
            {isMicLoading ? 'Starting Microphone...' : 'Turn On Microphone'}
          </Button>
        ) : (
          <div className="flex items-center text-success-600 bg-success-50 rounded-lg px-3 py-2">
            <Mic className="h-4 w-4 mr-2" />
            <span className="text-sm font-medium">🎙 Microphone Active</span>
          </div>
        )}
      </div>

      {/* Audio Test Section */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-medium text-gray-800 flex items-center">
            <Volume2 className="h-5 w-5 mr-2 text-primary-600" />
            Audio Test
          </h3>
          {audioBlocked && (
            <div className="flex items-center space-x-2">
              <VolumeX className="h-4 w-4 text-warning-500" />
              <span className="text-sm text-warning-600 font-medium">Blocked</span>
            </div>
          )}
        </div>
        
        <div className="space-y-3">
          <p className="text-sm text-gray-600">
            Test if voice feedback will work on your device
          </p>
          <Button
            variant="outline"
            onClick={() => {
              // Test audio with a simple beep
              const testAudio = async () => {
                try {
                  const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
                  
                  if (audioContext.state === 'suspended') {
                    await audioContext.resume();
                  }
                  
                  
                  const oscillator = audioContext.createOscillator();
                  const gainNode = audioContext.createGain();
                  
                  oscillator.connect(gainNode);
                  gainNode.connect(audioContext.destination);
                  
                  oscillator.frequency.value = 800;
                  gainNode.gain.setValueAtTime(0.1, audioContext.currentTime);
                  gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);
                  
                  oscillator.start(audioContext.currentTime);
                  oscillator.stop(audioContext.currentTime + 0.5);
                  
                  alert('Audio test successful! You should have heard a beep.');
                } catch (error) {
                  console.error('Audio test failed:', error);
                  alert('Audio blocked. Please check your device volume and permissions.');
                }
              };
              
              testAudio();
            }}
            leftIcon={<Volume2 className="h-4 w-4" />}
            fullWidth
          >
            🔊 Test Audio Playback
          </Button>
          
          {audioBlocked && (
            <div className="bg-warning-50 border border-warning-200 rounded-lg p-3">
              <div className="flex items-start">
                <VolumeX className="h-5 w-5 text-warning-600 mr-2 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-warning-800 text-sm font-medium">Audio Blocked</p>
                  <p className="text-warning-700 text-sm mt-1">
                    Voice feedback is blocked. Tap "Test Audio Playback" or any button to enable sound.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Session Status */}
      {isSessionActive && cameraActive && micActive && (
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-success-50 border border-success-200 rounded-lg p-4"
        >
          <div className="flex items-center">
            <CheckCircle className="h-5 w-5 text-success-600 mr-3" />
            <div>
              <p className="text-success-800 font-medium">✅ Session Ready</p>
              <p className="text-success-700 text-sm">Camera and microphone are active. You can start interacting!</p>
            </div>
          </div>
        </motion.div>
      )}

      {/* Permission Help */}
      {(cameraError || micError) && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h4 className="font-medium text-blue-800 mb-2">Need Help with Permissions?</h4>
          <div className="text-sm text-blue-700 space-y-2">
            <p><strong>Chrome/Edge:</strong> Click the camera/microphone icon in the address bar</p>
            <p><strong>Safari:</strong> Go to Safari → Settings → Websites → Camera/Microphone</p>
            <p><strong>Firefox:</strong> Click the shield icon and allow permissions</p>
            <p><strong>Mobile:</strong> Check your browser settings and allow camera/microphone access</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default MobileSessionControls;